<script>
  import { onMount } from "svelte";
  import eb from "#src/extensions/event-bus.js";
  import { tooltip } from "svelte-utilities";
  import {
    selectedLocations,
    selectedPages,
    hiddenLocations,
    hiddenPages,
    selectedDimensions,
  } from "#src/stores/ui.js";

  import XIcon from "@local/assets/icons/x.svg";
  import LocationItemIcon from "@local/assets/icons/location-item.svg";
  import LocationCollectionIcon from "@local/assets/icons/location-opening.svg";
  import LocationTypeIcon from "@local/assets/icons/location-type.svg";
  import { isLocationVisible } from "#src/extensions/filter-items.js";
  import NotesIcon from "@local/assets/icons/notes.svg";
  import DimensionIcon from "@local/assets/icons/dimension-linear.svg";
  import InvertIcon from "@local/assets/icons/invert.svg";
  import ShowIcon from "@local/assets/icons/show.svg";

  export let group;
  export let doc;
  export let locations;
  export let dimensions;
  export let hiddenLocationList;
  export let filters;

  let visible = false;

  function relatedRecord(location, grp) {
    if (!location) return null;

    if (location.record_type === "type") {
      return grp.types[location.record_id];
    } else if (location.record_type === "document_note") {
      return grp.document_notes[location.record_id];
    } else {
      return grp.items[location.record_id];
    }
  }

  function toggleVisible() {
    eb.dispatch("close-doc-panels", "quick-select");
    visible = !visible;
  }

  function closePanel(except) {
    if (except === "quick-select") return;
    visible = false;
  }

  function selectItemLocations() {
    const itemLocs = locations
      .filter((loc) => {
        const related = relatedRecord(loc, $group);
        const page = doc.data.pages[loc.page_id];
        return (
          related &&
          page &&
          loc.record_type === "item" &&
          !$hiddenLocations[loc.id] &&
          !$hiddenPages[loc.page_id] &&
          !page.hidden &&
          isLocationVisible(loc, $group, filters)
        );
      })
      .map((loc) => loc.id);

    $selectedPages = {};
    selectedLocations.select(...itemLocs);
  }

  function selectCollectionLocations() {
    const collectionLocs = locations
      .filter((loc) => {
        const related = relatedRecord(loc, $group);
        const page = doc.data.pages[loc.page_id];
        return (
          related &&
          page &&
          loc.record_type === "collection" &&
          !$hiddenLocations[loc.id] &&
          !$hiddenPages[loc.page_id] &&
          !page.hidden &&
          isLocationVisible(loc, $group, filters)
        );
      })
      .map((loc) => loc.id);

    $selectedPages = {};
    selectedLocations.select(...collectionLocs);
  }

  function selectTypeLocations() {
    const typeLocs = locations
      .filter((loc) => {
        const related = relatedRecord(loc, $group);
        const page = doc.data.pages[loc.page_id];
        return (
          related &&
          page &&
          loc.record_type === "type" &&
          !$hiddenLocations[loc.id] &&
          !$hiddenPages[loc.page_id] &&
          !page.hidden &&
          isLocationVisible(loc, $group, filters)
        );
      })
      .map((loc) => loc.id);

    $selectedPages = {};
    selectedLocations.select(...typeLocs);
  }

  function selectNotesLocations() {
    const noteLocs = locations
      .filter((loc) => {
        const related = relatedRecord(loc, $group);
        const page = doc.data.pages[loc.page_id];
        return (
          related &&
          page &&
          loc.record_type === "document_note" &&
          !$hiddenLocations[loc.id] &&
          !$hiddenPages[loc.page_id] &&
          !page.hidden &&
          isLocationVisible(loc, $group, filters)
        );
      })
      .map((loc) => loc.id);

    $selectedPages = {};
    selectedLocations.select(...noteLocs);
  }

  function selectDimensions() {
    if (!filters.show_dimensions) return;
    const dims = dimensions.map((dim) => dim.id);
    selectedDimensions.selectOnly(...dims);
  }

  function invertSelection() {
    const newSelection = locations
      .filter((loc) => {
        const related = relatedRecord(loc, $group);
        const page = doc.data.pages[loc.page_id];
        return (
          related &&
          page &&
          !$selectedLocations[loc.id] &&
          !$hiddenLocations[loc.id] &&
          !$hiddenPages[loc.page_id] &&
          !page.hidden &&
          isLocationVisible(loc, $group, filters)
        );
      })
      .map((loc) => loc.id);

    $selectedPages = {};
    selectedLocations.selectOnly(...newSelection);
  }

  function showAllLocations() {
    $hiddenLocations = {};
  }

  onMount(() => {
    eb.on("open-quick-select", toggleVisible);
    eb.on("close-doc-panels", closePanel);

    return () => {
      eb.unsubscribe("open-quick-select", toggleVisible);
      eb.unsubscribe("close-doc-panels", closePanel);
    };
  });
</script>

{#if visible}
  <div class="w-full flex justify-end p-4 absolute z-30 pointer-events-none text-xs">
    <div class="w-72 pointer-events-auto">
      <div class="bg-white rounded border shadow-lg w-full">
        <div class="p-2 flex items-center border-b">
          <div class="grow">Quick Select</div>
          <button on:click={toggleVisible} class="rounded p-1 hover:bg-gray-200">
            <XIcon />
          </button>
        </div>
        <div class="p-2 space-y-1">
          <div class="flex justify-between items-center">
            <div class="text-gray-400">Select Category</div>
            <div class="flex gap-1 items-center">
              <button
                class="rounded p-1 hover:bg-gray-200"
                use:tooltip={{ text: "Items" }}
                on:click={selectItemLocations}>
                <LocationItemIcon />
              </button>
              <button
                class="rounded p-1 hover:bg-gray-200"
                use:tooltip={{ text: "Openings" }}
                on:click={selectCollectionLocations}>
                <LocationCollectionIcon />
              </button>
              <button
                class="rounded p-1 hover:bg-gray-200"
                use:tooltip={{ text: "Types" }}
                on:click={selectTypeLocations}>
                <LocationTypeIcon />
              </button>
              <button
                class="rounded p-1 hover:bg-gray-200"
                use:tooltip={{ text: "Notes" }}
                on:click={selectNotesLocations}>
                <NotesIcon />
              </button>
              <button
                class="rounded p-1 hover:bg-gray-200"
                use:tooltip={{ text: "Dimensions" }}
                on:click={selectDimensions}>
                <DimensionIcon />
              </button>
            </div>
          </div>
          <div class="flex justify-between items-center">
            <div class="text-gray-400">Invert Selection</div>
            <div>
              <button
                class="rounded p-1 hover:bg-gray-200"
                use:tooltip={{ text: "Invert" }}
                on:click={invertSelection}>
                <InvertIcon />
              </button>
            </div>
          </div>
          {#if hiddenLocationList.length > 0}
            <div class="flex justify-between items-center">
              <div class="text-gray-400 italic">
                {hiddenLocationList.length} hidden locations.
              </div>
              <button
                class="rounded p-1 hover:bg-gray-200"
                use:tooltip={{ text: "Show All" }}
                on:click={showAllLocations}>
                <ShowIcon />
              </button>
            </div>
          {/if}
        </div>
      </div>
    </div>
  </div>
{/if}
